import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  validateEmail,
  formatNumericPhoneInput,
  validateName,
  validatePhone,
} from "../../utils/validation";
import { ValidationResult } from "../../utils/validation";
import LocationSelector from "../../components/LocationSelector";

const CandidateSignupPage: React.FC = () => {
  const { registerJobSeeker, loading } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string>("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [selectedPhoneCode, setSelectedPhoneCode] = useState<string>("");

  const [formData, setFormData] = useState<{
    email: string;
    first_name: string;
    last_name: string;
    country_code: string;
    phone: string;
    location: string;
    country: string;
    state: string;
    city: string;
  }>({
    email: "",
    first_name: "",
    last_name: "",
    country_code: "",
    phone: "",
    location: "",
    country: "",
    state: "",
    city: "",
  });

  const validateField = (name: string, value: string): ValidationResult => {
    switch (name) {
      case "first_name":
        return validateName(value, "First name");
      case "last_name":
        return validateName(value, "Last name");
      case "email":
        return validateEmail(value);
      case "phone":
        return validatePhone(value, true);
      default:
        return { isValid: true, message: "" };
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Format phone number as user types
    let formattedValue = value;
    if (name === "phone") {
      formattedValue = formatNumericPhoneInput(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));

    // Real-time validation
    const validation = validateField(name, formattedValue);
    setFieldErrors((prev) => ({
      ...prev,
      [name]: validation.isValid ? "" : validation.message,
    }));

    // Clear general error when user starts typing
    if (error) setError("");
  };

  const applyCountryPhoneCode = (phoneCode?: string) => {
    const normalizedPhoneCode = phoneCode || "";
    setSelectedPhoneCode(normalizedPhoneCode);
    setFormData((prev) => ({
      ...prev,
      country_code: normalizedPhoneCode,
    }));

    setFieldErrors((prev) => ({ ...prev, phone: "", country: "" }));
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    const firstNameValidation = validateName(formData.first_name, "First name");
    if (!firstNameValidation.isValid)
      errors.first_name = firstNameValidation.message;

    const lastNameValidation = validateName(formData.last_name, "Last name");
    if (!lastNameValidation.isValid)
      errors.last_name = lastNameValidation.message;

    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) errors.email = emailValidation.message;

    const phoneValidation = validatePhone(formData.phone, true);
    if (!phoneValidation.isValid) errors.phone = phoneValidation.message;

    if (!formData.country.trim()) errors.country = "Country is required";

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      setError("Please fix the errors above");
      return;
    }

    try {
      setError("");
      // Register job seeker - this will create account and send OTP
      await registerJobSeeker({
        ...formData,
        country_code: selectedPhoneCode,
        phone: formData.phone.trim(),
      });

      // Redirect to email verification page with job seeker flow
      navigate(
        `/auth/verify-email?email=${encodeURIComponent(
          formData.email
        )}&flow=job_seeker`
      );
    } catch (err: any) {
      console.error("Registration error:", err);
      console.error("Error response:", err.response?.data);

      // Better error message handling
      let errorMessage = "Registration failed";
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          // Handle validation errors
          errorMessage = err.response.data.detail
            .map((e: any) => e.msg || e.message || JSON.stringify(e))
            .join(", ");
        } else {
          errorMessage = err.response.data.detail;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Join as a Candidate
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Create your account and get discovered by top companies
          </p>
        </div>

        <div className="bg-white shadow-xl rounded-lg p-8">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="space-y-6">
            {/* First Name & Last Name */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="first_name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  First Name *
                </label>
                <input
                  id="first_name"
                  name="first_name"
                  type="text"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm ${
                    fieldErrors.first_name
                      ? "border-red-300 bg-red-50"
                      : "border-gray-300"
                  }`}
                />
                {fieldErrors.first_name && (
                  <p className="mt-1 text-xs text-red-600">
                    {fieldErrors.first_name}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="last_name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Last Name *
                </label>
                <input
                  id="last_name"
                  name="last_name"
                  type="text"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm ${
                    fieldErrors.last_name
                      ? "border-red-300 bg-red-50"
                      : "border-gray-300"
                  }`}
                />
                {fieldErrors.last_name && (
                  <p className="mt-1 text-xs text-red-600">
                    {fieldErrors.last_name}
                  </p>
                )}
              </div>
            </div>

            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email Address *
              </label>
              <input
                id="email"
                name="email"
                type="text"
                required
                value={formData.email}
                onChange={handleInputChange}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm ${
                  fieldErrors.email
                    ? "border-red-300 bg-red-50"
                    : "border-gray-300"
                }`}
                placeholder="you@email.com"
              />
              {fieldErrors.email && (
                <p className="mt-1 text-xs text-red-600">{fieldErrors.email}</p>
              )}
            </div>

            {/* Location */}
            <div>
              <LocationSelector
                id="location"
                name="location"
                required
                value={formData.location}
                countryError={fieldErrors.country}
                onCountryChange={(country) =>
                  applyCountryPhoneCode(country?.phone_code)
                }
                renderAfterCountry={
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label
                        htmlFor="country_code"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Country Code
                      </label>
                      <input
                        id="country_code"
                        name="country_code"
                        type="text"
                        value={formData.country_code}
                        readOnly
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-sm"
                        placeholder="+1"
                      />
                    </div>
                    <div className="col-span-2">
                      <label
                        htmlFor="phone"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Phone Number *
                      </label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        inputMode="numeric"
                        className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm ${
                          fieldErrors.phone
                            ? "border-red-300 bg-red-50"
                            : "border-gray-300"
                        }`}
                        placeholder="5551234567"
                      />
                      {fieldErrors.phone && (
                        <p className="mt-1 text-xs text-red-600">
                          {fieldErrors.phone}
                        </p>
                      )}
                    </div>
                  </div>
                }
                onChange={(value) => {
                  setFormData((prev) => ({ ...prev, location: value }));
                  // Clear general error when user starts typing
                  if (error) setError("");
                }}
                onLocationPartsChange={(parts) => {
                  setFormData((prev) => ({
                    ...prev,
                    country: parts.country,
                    state: parts.state,
                    city: parts.city,
                  }));
                  if (error) setError("");
                }}
              />
              {fieldErrors.location && (
                <p className="mt-1 text-xs text-red-600">
                  {fieldErrors.location}
                </p>
              )}
            </div>

            {/* Benefits Section */}
            <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
              <h4 className="text-sm font-semibold text-emerald-800 mb-2 flex items-center">
                <svg
                  className="w-4 h-4 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                What you get with TalentKey.AI:
              </h4>
              <ul className="text-xs text-emerald-700 space-y-1">
                <li>• AI-powered profile creation from your resume</li>
                <li>• Get discovered by top companies actively hiring</li>
                <li>• Track your profile visibility and search analytics</li>
                <li>• Smart resume parsing and skill extraction</li>
                <li>• Optional verification services for premium visibility</li>
              </ul>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Creating Account..." : "Create Account"}
            </button>

            {/* Sign In Link */}
            <div className="text-center">
              <p className="text-sm text-gray-600">
                Already have an account?{" "}
                <Link
                  to="/auth/login"
                  className="font-medium text-emerald-600 hover:text-emerald-500 transition-colors"
                >
                  Sign in
                </Link>
              </p>
              <p className="text-xs text-gray-500 mt-4">
                By creating an account, you agree to our{" "}
                <Link
                  to="/terms"
                  className="text-emerald-600 hover:text-emerald-500 underline"
                >
                  Terms & Conditions
                </Link>{" "}
                and{" "}
                <Link
                  to="/privacy"
                  className="text-emerald-600 hover:text-emerald-500 underline"
                >
                  Privacy Policy
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CandidateSignupPage;
